'use client';
import { useEffect, useState } from 'react';
import type { RootState } from '@/store/store';
import { useStore } from '@/store/store';
import { retrieve, getSessionId } from '@/utils/storage';
import { LOCAL_STORAGE } from '@/utils/constants';
import {
    setDYBCounterInUserPreferences,
    setDYBV2CounterInUserPreferences,
    getDYBUserPreferences,
    DYB_INTERSTITIAL_MAX_TIMES_SHOWN
} from '@/logic/dyb-interstatial/dyb-interstatial-helper';
import { useGlobalContext } from '@/store/contexts/global-context';

export const useShowDYBInterstitial = (
    isUserLoggedIn: boolean,
    siteWideJackpotOn: boolean,
    exclusiveZoneEnabled: boolean = false,
    hasAtLeastOneExclusiveSeen: boolean = false,
    isSWJTutorialV2: boolean = false
) => {
    const [showDYBInterstitial, setShowDYBInterstitial] = useState(false);

    const optedInStatus = useStore((state: RootState) => state.siteWideJackpot.multiplier);
    const hasSwjInitialized = useStore((state: RootState) => state.siteWideJackpot.hasSwjInitialized);
    const showDYBTutorialCounter = useStore((state: RootState) => state.siteWideJackpot.showDYBTutorialCounter);
    const showDYBTutorialV2Counter = useStore((state: RootState) => state.siteWideJackpot.showDYBTutorialV2Counter);
    const setDYBUserPreferences = useStore((state: RootState) => state.siteWideJackpot.setDYBUserPreferences);
    const tutorialCounter = isSWJTutorialV2 ? showDYBTutorialV2Counter : showDYBTutorialCounter;

    const { isMobile } = useGlobalContext();
    // Fetch user preferences when the interstitial is triggered (after login/initialization)
    useEffect(() => {
        if (isUserLoggedIn) {
            getDYBUserPreferences().then((preferences) => {
                if (!preferences) return;
                setDYBUserPreferences({
                    ...preferences,
                    showDYBTracker: preferences.showDYBTracker ?? true
                });
            });
        }
    }, [isUserLoggedIn, setDYBUserPreferences]);

    // Update tutorial counter when user opts in
    useEffect(() => {
        if (
            optedInStatus !== 0 &&
            tutorialCounter !== undefined &&
            tutorialCounter < DYB_INTERSTITIAL_MAX_TIMES_SHOWN
        ) {
            if (isSWJTutorialV2) {
                setDYBV2CounterInUserPreferences(DYB_INTERSTITIAL_MAX_TIMES_SHOWN);
            } else {
                setDYBCounterInUserPreferences(DYB_INTERSTITIAL_MAX_TIMES_SHOWN);
            }
        }
    }, [optedInStatus, tutorialCounter, isSWJTutorialV2]);

    // Check if we should show the interstitial
    useEffect(() => {
        if (!isUserLoggedIn || hasSwjInitialized === undefined) {
            return;
        }

        const currentSession = getSessionId();
        if (!currentSession) {
            return;
        }

        const expectingExclusivePopup = isMobile && exclusiveZoneEnabled && !hasAtLeastOneExclusiveSeen;

        // Check if interstitial was shown this session
        const interstitialLastSeenInSession = retrieve(LOCAL_STORAGE.DYB_INTERSTITIAL_SHOWN, 'local');
        const interstitialSeenThisSession = currentSession === interstitialLastSeenInSession;

        // Check if exclusive popup was shown this session (on mobile)
        const exclusiveLastSeenInSession = retrieve(LOCAL_STORAGE.EXCLUSIVE_MODAL_SEEN_THIS_SESSION, 'local');
        const exclusiveSeenThisSession = isMobile && currentSession === exclusiveLastSeenInSession;

        // Only show if:
        // 1. Site wide jackpot is enabled
        // 2. User is not opted in (optedInStatus === 0)
        // 3. Interstitial hasn't been shown this session
        // 4. Exclusive popup hasn't been shown this session (on mobile)
        // 5. Not expecting exclusive popup (exclusive zones take priority)
        if (
            siteWideJackpotOn &&
            !interstitialSeenThisSession &&
            !exclusiveSeenThisSession &&
            optedInStatus === 0 &&
            !expectingExclusivePopup
        ) {
            setShowDYBInterstitial(true);
        }
    }, [
        isUserLoggedIn,
        hasSwjInitialized,
        siteWideJackpotOn,
        exclusiveZoneEnabled,
        hasAtLeastOneExclusiveSeen,
        isMobile
    ]);

    return { showDYBInterstitial, setShowDYBInterstitial };
};
